import styled, { useTheme } from 'styled-components';
import { useCreateApplePayLinkMutation } from '../../../redux/api/authorized/deposit';
import { toast } from 'react-toastify';
import { useEffect, useState } from 'react';
import { ApplePayWidget } from '../../../components/ApplePayWidget';
import { PHONE_MEDIA_QUERY } from '../../../common/responsive';
import ApplePayButton from '../../../components/ApplePayButton';
import Modal from '../../../components/Modal';
import { Heading3Text, ParagraphLargeText } from '../../../components/Texts';
import { formatUSD } from '../../../common/currency';
import UsdcIcon from '../../../assets/icons/usdc.svg?react';
import LockIcon from '../../../assets/icons/lock.svg?react';
import { useSolana } from '../../../hooks/useSolana';
import { useDispatch } from 'react-redux';
import { openModal } from '../../../redux/slices/modals';

const ContentContainer = styled.div`
  display: flex;
  gap: 20px;
  flex-direction: column;
  width: 100%;
  max-width: 1279px;
  box-sizing: border-box;
  text-align: left;
  @media ${PHONE_MEDIA_QUERY} {
    gap: 32px;
  }
`;

const HighCohesionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const Line = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.border.secondary};
  width: 100%;
`;

const ApplePayModal = ({ onClose, amount }) => {
  const [createApplePayLink] = useCreateApplePayLinkMutation();
  const [paymentUrl, setPaymentUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const [toastAlertShown, setToastAlertShown] = useState(false);
  const [toastAlert, setToastAlert] = useState(null);
  const { refreshTokenBalances } = useSolana();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!toastAlertShown && toastAlert) {
      if (toastAlert.type === 'success') {
        toast.success(toastAlert.message);
        onClose();
      } else if (toastAlert.type === 'error') {
        dispatch(openModal({ name: 'paymentFailed' }));
      } else if (toastAlert.type === 'info') {
        toast.info(toastAlert.message);
        onClose();
      }
      setToastAlertShown(true);
    }
  }, [toastAlert, toastAlertShown]);

  return (
    <Modal onClose={onClose}>
      <ContentContainer>
        <ParagraphLargeText style={{ textAlign: 'center', width: '100%' }}>
          Deposit
        </ParagraphLargeText>
        <HighCohesionContainer>
          <UsdcIcon width={40} height={40} style={{ marginBottom: 10 }} />
          <Heading3Text
            style={{ textAlign: 'center', width: '100%', fontWeight: 600 }}
          >
            Buy {formatUSD(amount)} of USDC
          </Heading3Text>
          <ParagraphLargeText
            style={{
              textAlign: 'center',
              width: '100%',
              color: theme.text.secondary,
            }}
          >
            USDC price: $1.00
          </ParagraphLargeText>
        </HighCohesionContainer>
        <ApplePayButton
          onClick={async () => {
            try {
              setLoading(true);
              const response = await createApplePayLink({
                paymentAmount: amount,
              });
              if (response.error) {
                toast.error('Failed to create Apple Pay link');
                setLoading(false);
              } else {
                setPaymentUrl(response.data.paymentLink.url);
              }
            } catch (error) {
              setLoading(false);
              toast.error('Failed to create Apple Pay link');
            } finally {
            }
          }}
          loading={loading}
        />
        <HighCohesionContainer style={{ gap: 10 }}>
          <Line />
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <LockIcon width={16} height={16} />
            <ParagraphLargeText
              style={{
                textAlign: 'center',
                width: '100%',
                fontWeight: 500,
                fontSize: 12,
                color: theme.text.secondary,
              }}
            >
              Secured by{' '}
              <span style={{ color: theme.primary.primary }}>Coinbase</span>
            </ParagraphLargeText>
          </div>
        </HighCohesionContainer>
        {paymentUrl && (
          <ApplePayWidget
            paymentUrl={paymentUrl}
            onClose={onClose}
            setIsProcessingPayment={(loading) => {
              console.log('setIsProcessingPayment', loading);
            }}
            setTransactionStatus={(status) => {
              if (status === 'success') {
                setToastAlert({
                  type: 'success',
                  message: 'Payment Successful',
                });
                refreshTokenBalances();
              } else if (status === 'pending') {
                // Here we just show the loading spinner in the button
              }
            }}
            onAlert={(title, message, type) => {
              if (type === 'error') {
                setToastAlert({ type: 'error', message: title });
              } else if (type === 'info') {
                setToastAlert({ type: 'info', message: title });
              }
            }}
          />
        )}
      </ContentContainer>
    </Modal>
  );
};

export default ApplePayModal;
